/**
 * Maps backend Term Sheet extraction keys (shape returned by the synply-api
 * term sheet extractor in `containers[0].data`) into a grouped, labelled
 * review structure for the Term Sheet Review dialog.
 *
 * Only fields with a non-empty value are emitted — low-confidence / missing
 * fields come back as `null` from the backend and are dropped here.
 */

export type TermSheetFieldType = 'string' | 'number' | 'date' | 'percent';

export interface TermSheetFieldDef {
  key: string;
  label: string;
  type: TermSheetFieldType;
}

export interface TermSheetReviewField {
  key: string;
  label: string;
  type: TermSheetFieldType;
  value: string;
}

export interface TermSheetReviewSection {
  title: string;
  fields: TermSheetReviewField[];
}

export interface SubFacilityReview {
  name: string | null;
  productType: string | null;
  industry: string | null;
  sections: TermSheetReviewSection[];
  fieldCount: number;
  raw: Record<string, string | null>;
}

export interface TermSheetExtractionResult {
  dealName: string | null;
  productType: string | null;
  industry: string | null;
  sections: TermSheetReviewSection[];
  fieldCount: number;
  /** Raw extracted payload, forwarded on submit */
  raw: Record<string, string | null>;
  /** Additional facilities parsed from containers[1..N] */
  subFacilities: SubFacilityReview[];
}

const SECTIONS: { title: string; fields: TermSheetFieldDef[] }[] = [
  {
    title: 'Deal Info',
    fields: [
      { key: 'dealName', label: 'Deal Name', type: 'string' },
      { key: 'c.pr', label: 'Product', type: 'string' },
      { key: 'c.sTp', label: 'Transaction Type', type: 'string' },
      { key: 'c.uTp', label: 'Underwriting Type', type: 'string' },
      { key: 'c.loanPurpose', label: 'Loan Purpose', type: 'string' },
      { key: 'c.indu', label: 'Industry', type: 'string' },
      { key: 'c.usState', label: 'US State or Territory', type: 'string' },
      { key: 'c.projectType', label: 'Project Type', type: 'string' },
      { key: 'c.crePropertyClass', label: 'Property Class', type: 'string' },
      {
        key: 'b.privateEquitySponsored',
        label: 'Private Equity Sponsored',
        type: 'string',
      },
      { key: 'b.naics', label: 'NAICS Code', type: 'string' },
      { key: 'b.borrowerRevenueSize', label: 'Revenue Size', type: 'string' },
      { key: 'b.ebitda', label: 'EBITDA', type: 'number' },
      { key: 'b.leverageRatio', label: 'Leverage Ratio', type: 'number' },
    ],
  },
  {
    title: 'Facility Structure',
    fields: [
      { key: 'c.numberOfLenders', label: 'Number of Lenders', type: 'number' },
      { key: 'c.oAmt', label: 'Total Facility Amount', type: 'number' },
      { key: 'c.trm', label: 'Term (Months)', type: 'number' },
      { key: 'c.amtz', label: 'Amortization (Months)', type: 'number' },
      { key: 't.iop', label: 'Interest Only Period (Months)', type: 'number' },
      { key: 'd.drawPrd', label: 'Draw Period (Months)', type: 'number' },
      { key: 'c.pmtFrq', label: 'Payment Frequency', type: 'string' },
      { key: 'c.ddConv', label: 'Day Count Convention', type: 'string' },
      {
        key: 'c.expectedUtilization',
        label: 'Expected Utilization',
        type: 'string',
      },
      { key: 'c.launchDt', label: 'Launch Date', type: 'date' },
      { key: 'c.mandateDt', label: 'Mandate Date', type: 'date' },
      { key: 'c.commitDt', label: 'Commitment Date', type: 'date' },
      { key: 'c.allocDt', label: 'Allocation Date', type: 'date' },
      { key: 'c.cDt', label: 'Closed Date', type: 'date' },
      { key: 'c.maturityDt', label: 'Maturity Date', type: 'date' },
    ],
  },
  {
    title: 'Pricing',
    fields: [
      {
        key: 'f.interestRateType',
        label: 'Interest Rate Type',
        type: 'string',
      },
      { key: 'c.indx', label: 'Index', type: 'string' },
      // c.sprd is `format: percentage` on the backend (common.json) — schema
      // stores it as a decimal (0.0575) and renders ×100 (5.75%). The LLM
      // returns it as a percent number per the term-sheet prompt, so we must
      // normalize via parseFieldValue('percent') on submit. Mislabeling this
      // as 'number' caused the 575% display bug (SD-2030).
      { key: 'c.sprd', label: 'Spread (%)', type: 'percent' },
      { key: 'c.pp', label: 'Purchase Price (%)', type: 'percent' },
      { key: 'c.initInrRte', label: 'Initial Interest Rate', type: 'percent' },
      { key: 'c.rrf', label: 'Rate Reset Frequency', type: 'string' },
      {
        key: 'f.interestRateFloorType',
        label: 'Interest Rate Floor Type',
        type: 'string',
      },
      { key: 'f.whatIsTheFloorRate', label: 'Floor Rate (%)', type: 'percent' },
      {
        key: 'f.performanceBasedPricing',
        label: 'Performance-Based Pricing',
        type: 'string',
      },
      {
        key: 'f.interestRateHedge',
        label: 'Interest Rate Hedge',
        type: 'string',
      },
    ],
  },
  {
    title: 'Fees',
    fields: [
      { key: 'f.origFee', label: 'Origination Fee (%)', type: 'percent' },
      { key: 'f.aFee', label: 'Arrangement Fee (%)', type: 'percent' },
      { key: 'd.unFee', label: 'Unused Fee (%)', type: 'percent' },
      { key: 'd.aRte', label: 'Advance Rate (%)', type: 'percent' },
      { key: 'd.unBlnc', label: 'Unused Balance', type: 'number' },
      {
        key: 'f.prepaymentPenaltyFee',
        label: 'Prepayment Penalty Fee',
        type: 'string',
      },
    ],
  },
  {
    title: 'Collateral & Covenants',
    fields: [
      { key: 'c1.loanToValue', label: 'Loan to Value (%)', type: 'percent' },
      { key: 'c1.loanToCost', label: 'Loan to Cost (%)', type: 'percent' },
      { key: 'c1.debtYield', label: 'Debt Yield (%)', type: 'percent' },
      { key: 'c1.recourseLevel', label: 'Recourse Level', type: 'string' },
      {
        key: 'g.guarantorLiabilityBurndown',
        label: 'Guarantor Liability Burndown',
        type: 'string',
      },
    ],
  },
];

const formatDate = (raw: string): string => {
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatNumber = (raw: string): string => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString('en-US');
};

export const formatTermSheetValue = (
  raw: string,
  type: TermSheetFieldType
): string => {
  if (raw == null) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';
  switch (type) {
    case 'date':
      return formatDate(trimmed);
    case 'number':
      return formatNumber(trimmed);
    case 'percent':
      return trimmed.endsWith('%') ? trimmed : `${trimmed}%`;
    default:
      return trimmed;
  }
};

const hasValue = (raw: unknown): raw is string => {
  if (raw === null || raw === undefined) return false;
  const trimmed = String(raw).trim();
  return trimmed !== '' && trimmed.toLowerCase() !== 'null';
};

const buildSectionsFromData = (
  data: Record<string, string | null>,
  sectionDefs: { title: string; fields: TermSheetFieldDef[] }[]
): { sections: TermSheetReviewSection[]; fieldCount: number } => {
  const sections: TermSheetReviewSection[] = [];
  let fieldCount = 0;

  for (const section of sectionDefs) {
    const fields: TermSheetReviewField[] = [];
    for (const def of section.fields) {
      const raw = data[def.key];
      if (!hasValue(raw)) continue;
      fields.push({
        key: def.key,
        label: def.label,
        type: def.type,
        value: formatTermSheetValue(raw, def.type),
      });
      fieldCount++;
    }
    if (fields.length > 0) {
      sections.push({ title: section.title, fields });
    }
  }

  return { sections, fieldCount };
};

/**
 * Build a grouped, editable review structure from the backend extraction payload.
 * Fields with null/empty values are omitted. Sections that end up empty are omitted.
 * `subFacilityContainers` are additional `containers[1..N]` when the term sheet
 * describes multiple facilities/tranches.
 */
export const buildTermSheetReview = (
  data: Record<string, string | null>,
  subFacilityContainers?: Record<string, string | null>[]
): TermSheetExtractionResult => {
  const { sections, fieldCount } = buildSectionsFromData(data, SECTIONS);

  const subFacilities: SubFacilityReview[] = [];
  if (subFacilityContainers?.length) {
    for (const sfData of subFacilityContainers) {
      subFacilities.push(buildSubFacilityReview(sfData));
    }
  }

  return {
    dealName: hasValue(data['dealName'])
      ? String(data['dealName']).trim()
      : null,
    productType: hasValue(data['c.pr']) ? String(data['c.pr']).trim() : null,
    industry: hasValue(data['c.indu']) ? String(data['c.indu']).trim() : null,
    sections,
    fieldCount,
    raw: data,
    subFacilities,
  };
};

const SUB_FACILITY_SECTIONS = SECTIONS.filter(s => s.title !== 'Deal Info');

const buildSubFacilityReview = (
  data: Record<string, string | null>
): SubFacilityReview => {
  const { sections, fieldCount } = buildSectionsFromData(
    data,
    SUB_FACILITY_SECTIONS
  );
  return {
    name: hasValue(data['dealName']) ? String(data['dealName']).trim() : null,
    productType: hasValue(data['c.pr']) ? String(data['c.pr']).trim() : null,
    industry: hasValue(data['c.indu']) ? String(data['c.indu']).trim() : null,
    sections,
    fieldCount,
    raw: data,
  };
};
